import { useState } from 'react';
import type { ACCESSEligibilityResultCode } from '@/types/registration';
import { useWregDesktop } from './useWregDesktop';
import { WregFlagsReadout } from './WregFlagsReadout';
import { WREG_ELIG_CODES } from './wregEligibility';
import {
  WREG_SCENARIOS,
  WREG_SCENARIO_END,
  type WregDisclosure,
  type WregScenario,
  type WregScenarioId,
} from './wregScenarios';

export const isWregDevPanelEnabled = (): boolean =>
  import.meta.env.DEV || import.meta.env.MODE === 'e2e';

type Props = {
  scenario: WregScenarioId;
  onScenario: (id: WregScenarioId) => void;
  code: ACCESSEligibilityResultCode;
  onCode: (code: ACCESSEligibilityResultCode) => void;
  live: boolean;
  onLive: (live: boolean) => void;
  disclosure: WregDisclosure;
  onDisclosure: (value: WregDisclosure) => void;
  onEnd: (id: WregScenarioId) => void;
};

const groupScenarios = (): { name: string; items: WregScenario[] }[] => {
  const groups: { name: string; items: WregScenario[] }[] = [];
  WREG_SCENARIOS.forEach((s) => {
    const existing = groups.find((g) => g.name === s.group);
    if (existing) existing.items.push(s);
    else groups.push({ name: s.group, items: [s] });
  });
  return groups;
};

const WregDevPanel = ({
  scenario,
  onScenario,
  code,
  onCode,
  live,
  onLive,
  disclosure,
  onDisclosure,
  onEnd,
}: Props) => {
  const isDesktop = useWregDesktop();
  const [open, setOpen] = useState(isDesktop);
  if (!isWregDevPanelEnabled()) return null;
  return (
    <div className={`reg-dev wreg-dev ${open ? '' : 'collapsed'}`}>
      <div className="reg-dev-head" onClick={() => setOpen(!open)}>
        <span className="reg-dev-title">◆ Scenario</span>
        <span className="reg-dev-chev">▾</span>
      </div>
      <div className="reg-dev-body">
        <WregFlagsReadout />
        <div className="wreg-sc-list">
          {groupScenarios().map((g) => (
            <div className="wreg-sc-group" key={g.name}>
              <div className="wreg-sc-gname">{g.name}</div>
              {g.items.map((s) => (
                <div className="wreg-sc-row" key={s.id}>
                  <button
                    className={`wreg-sc-item ${scenario === s.id ? 'on' : ''}`}
                    onClick={() => onScenario(s.id)}
                  >
                    {s.label}
                  </button>
                  {WREG_SCENARIO_END[s.id] && (
                    <button
                      className="wreg-sc-end"
                      title="Jump to this scenario's end screen"
                      onClick={() => onEnd(s.id)}
                    >
                      End ↦
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="reg-dev-group">
          <span className="reg-dev-label">Control-group disclosure</span>
          <div className="reg-dev-seg">
            <button
              className={`reg-dev-opt ${disclosure === 'A' ? 'on' : ''}`}
              onClick={() => onDisclosure('A')}
            >
              A · hide
            </button>
            <button
              className={`reg-dev-opt ${disclosure === 'B' ? 'on' : ''}`}
              onClick={() => onDisclosure('B')}
            >
              B · disabled card
            </button>
          </div>
        </div>
        <div className="reg-dev-group">
          <span className="reg-dev-label">Eligibility result code</span>
          <div className="wreg-sc-list">
            {WREG_ELIG_CODES.map((c) => (
              <button
                key={c}
                className={`wreg-code-item ${code === c ? 'on' : ''}`}
                onClick={() => onCode(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="reg-dev-group">
          <span className="reg-dev-label">CMS eligibility + alignment calls</span>
          <div className="reg-dev-seg">
            <button className={`wreg-cms-opt ${live ? '' : 'on'}`} onClick={() => onLive(false)}>
              Mock
            </button>
            <button className={`wreg-cms-opt ${live ? 'on' : ''}`} onClick={() => onLive(true)}>
              Live
            </button>
          </div>
        </div>
        <div className="reg-dev-note">
          Open question, pending Jessica: whether a blocked control-group track must be disclosed.
        </div>
      </div>
    </div>
  );
};

export { WregDevPanel };
